import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';

import { of } from 'rxjs';
import { first, map, tap, withLatestFrom } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { environment } from '@rocksoft/environment';
import { CategoryType, EnvironmentFeatures } from '@rocksoft/shared';
import { CategoryStoreActions } from '../category';
import { LocationsRouterStoreSelectors } from '../router/index';

export const categoryResolver: (categoryType: CategoryType) => ResolveFn<boolean> = (categoryType: CategoryType) => {
  return () => {
    const store = inject(Store);
    const features: EnvironmentFeatures = environment.features;

    // Check feature flags for categories that require them
    const featureEnabled = (() => {
      switch (categoryType) {
        case 'Bar':
          return features.bar?.enabled ?? true;
        case 'FrontDesk':
          return features.frontdesk?.enabled ?? true;
        case 'Housekeeping':
          return features.housekeeping?.enabled ?? true;
        case 'Kiosk':
          return features.kiosk?.enabled ?? true;
        case 'Staff':
          return features.staff?.enabled ?? true;
        case 'Location':
        case 'Room':
          return true; // No feature flag check for Location and Room
        default:
          return true;
      }
    })();

    if (!featureEnabled) {
      return of(true);
    }

    // Handle Brand: uses brandId, dispatches categoriesRequested
    if (categoryType === 'Brand') {
      return store.select(LocationsRouterStoreSelectors.selectBrandHumanId).pipe(
        tap(() => {
          store.dispatch(
            CategoryStoreActions.CategoryResolverActions.categoriesRequested({
              categoryType: 'Brand',
            })
          );
        }),
        map(() => true),
        first()
      );
    }

    // Handle Location: uses brandId, dispatches categoriesRequested
    if (categoryType === 'Location') {
      return store.select(LocationsRouterStoreSelectors.selectBrandHumanId).pipe(
        tap(() => {
          store.dispatch(
            CategoryStoreActions.CategoryResolverActions.categoriesRequested({
              categoryType: 'Location',
            })
          );
        }),
        map(() => true),
        first()
      );
    }

    // Handle Room: uses locationId and brandId, dispatches categoriesRequested
    if (categoryType === 'Room') {
      return store.select(LocationsRouterStoreSelectors.selectLocationHumanId).pipe(
        withLatestFrom(store.select(LocationsRouterStoreSelectors.selectBrandHumanId)),
        tap(([locationHumanId]) => {
          if (locationHumanId) {
            store.dispatch(
              CategoryStoreActions.CategoryResolverActions.categoriesRequested({
                categoryType: 'Room',
                locationId: locationHumanId,
              })
            );
          }
        }),
        map(() => true),
        first()
      );
    }

    // Handle Bar, FrontDesk, Housekeeping, Kiosk, Staff: use locationId, dispatch categoryRequested
    return store.select(LocationsRouterStoreSelectors.selectLocationHumanId).pipe(
      tap((locationId) => {
        if (locationId) {
          store.dispatch(
            CategoryStoreActions.CategoryResolverActions.categoriesRequested({
              categoryType,
              locationId: locationId,
            })
          );
        }
      }),
      map(() => true),
      first()
    );
  };
};
